import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { Button } from "@/components/ui/button";
import { TextField } from "@fleet/ui";
import { AnimatedSection } from "@/components/ui/animated-section";
import { CalendarClock, Plus, Tag } from "@fleet/icons";
import { useI18n } from "@/providers/i18n-provider";

/** Preview of the tokens detected by the deterministic quick-add parser. */
interface QuickAddPreview {
  /** Human-readable matched date span, e.g. "tomorrow 6pm". */
  dateLabel: string | null;
  /** Whether the detected date carried an explicit clock time. */
  hasTime: boolean;
  /** ISO due date that would be applied. */
  dueDate: string | null;
  /** Raw `#tag`/`@list` token name. */
  listToken: string | null;
  /** Resolved list name when the token matched an existing list. */
  matchedListName: string | null;
}

interface TodoListAddBarProps {
  /** When false the bar is hidden via AnimatedSection */
  show: boolean;
  value: string;
  isSubmitting: boolean;
  /**
   * When true the action can't run yet (e.g. the local DB isn't ready), so the
   * input and the Add button stay disabled to avoid a no-op submit.
   */
  disabled?: boolean;
  onInput: (value: string) => void;
  onSubmit: (e: Event) => void;
  /** Ref callback so the parent can focus the input programmatically */
  inputRef?: (el: HTMLInputElement) => void;
  /** Live preview of inline NL tokens, or null when nothing detected. */
  preview?: QuickAddPreview | null;
}

/** Formats an ISO due date for the preview chip (locale-aware, short). */
function formatDueLabel(iso: string, hasTime: boolean): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(hasTime ? { hour: "numeric", minute: "2-digit" } : {}),
  });
}

export function TodoListAddBar(props: TodoListAddBarProps) {
  const { t } = useI18n();

  return (
    <div class="shrink-0 border-t border-border/40 bg-background/95 backdrop-blur-sm px-6 pb-[calc(env(safe-area-inset-bottom,0px))] z-20">
      <AnimatedSection show={props.show}>
        <div class="py-2.5">
          {/* Deterministic quick-add preview: shows the date/list parsed from
              the inline natural-language tokens before the task is created. */}
          <Show when={props.preview}>
            {(preview) => (
              <div
                class="flex flex-wrap items-center gap-1.5 pb-2"
                data-testid="quick-add-preview"
                aria-live="polite"
              >
                <Show when={preview().dueDate}>
                  {(due) => (
                    <span
                      class="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary"
                      data-testid="quick-add-preview-date"
                    >
                      <CalendarClock class="h-3 w-3" />
                      {formatDueLabel(due(), preview().hasTime)}
                    </span>
                  )}
                </Show>
                <Show when={preview().listToken}>
                  {(token) => (
                    <span
                      class="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground/80"
                      data-testid="quick-add-preview-list"
                    >
                      <Tag class="h-3 w-3" />
                      {preview().matchedListName ?? token()}
                    </span>
                  )}
                </Show>
              </div>
            )}
          </Show>
          <form
            onSubmit={(e) => {
              props.onSubmit(e);
            }}
            class="flex gap-2"
          >
            <div class="relative flex-1 group/input">
              <TextField
                ref={props.inputRef}
                placeholder={t(msg`Add a task...`)}
                value={props.value}
                onInput={(e) => {
                  props.onInput(e.currentTarget.value);
                }}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    props.onSubmit(e);
                  }
                }}
                class="w-full pr-10"
                disabled={props.isSubmitting || props.disabled}
              />
              {/* Keyboard shortcut hint — fades out when input is focused */}
              <div
                class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center select-none cursor-help group-focus-within/input:opacity-0 transition-opacity duration-150"
                title={t(msg`Quick focus: Press N to focus the add task input field`)}
              >
                <kbd class="h-4.5 min-w-[16px] inline-flex items-center justify-center rounded border border-border/60 bg-background px-1.5 font-mono text-[9px] font-bold text-foreground/80 shadow-sm">
                  N
                </kbd>
              </div>
            </div>

            <Button
              type="submit"
              disabled={!props.value.trim() || props.isSubmitting || props.disabled}
            >
              <Plus class="mr-2 h-4 w-4" />
              {t(msg`Add`)}
            </Button>
          </form>
        </div>
      </AnimatedSection>
    </div>
  );
}
